/**
 * ServerDataSection Component
 *
 * Accordion section for defining server-injected data parameters.
 * Part of the "DEFINE → USE" pattern: defines parameters that can be used in destinations.
 *
 * Features:
 * - Autocomplete with preset server sources (geoData.country, ip, etc.)
 * - Custom path support for advanced users
 * - Auto-suggests internal key based on server path
 *
 * @module webplacements/components/editors/sources
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import StorageIcon from '@mui/icons-material/Storage';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { ParameterDefinition } from '@modules/webplacements/types/webplacements.types';
import { PRESET_SERVER_SOURCES } from '@modules/webplacements/types/webplacements.types';

export interface ServerDataSectionProps {
  /** Current server data parameters (filtered from all parameters) */
  parameters: ParameterDefinition[];
  /** Called when parameters change */
  onChange: (params: ParameterDefinition[]) => void;
  /** Whether the editor is disabled */
  disabled?: boolean;
}

interface NewParamState {
  serverPath: string;
  key: string;
}

const initialNewParam: NewParamState = {
  serverPath: '',
  key: '',
};

/**
 * ServerDataSection Component
 *
 * Provides a table-based interface for defining server data parameters.
 * Each row maps a server data path to an internal key.
 */
export const ServerDataSection: React.FC<ServerDataSectionProps> = ({
  parameters,
  onChange,
  disabled = false,
}) => {
  const theme = useTheme();
  const [newParam, setNewParam] = useState<NewParamState>(initialNewParam);
  const [isAdding, setIsAdding] = useState(false);

  const handleAddClick = useCallback(() => {
    setIsAdding(true);
    setNewParam(initialNewParam);
  }, []);

  const handleCancelAdd = useCallback(() => {
    setIsAdding(false);
    setNewParam(initialNewParam);
  }, []);

  const handleSaveNew = useCallback(() => {
    if (!newParam.serverPath || !newParam.key) return;

    // Check for duplicate keys
    if (parameters.some((p) => p.key === newParam.key)) {
      return; // Could show error toast here
    }

    // Find preset for label and dataType
    const preset = PRESET_SERVER_SOURCES.find((p) => p.path === newParam.serverPath);

    const newDefinition: ParameterDefinition = {
      key: newParam.key,
      source: 'server',
      sourceDetails: {
        serverPath: newParam.serverPath,
      },
      label: preset?.label || newParam.serverPath,
      dataType: preset?.dataType || 'string',
    };

    onChange([...parameters, newDefinition]);
    setIsAdding(false);
    setNewParam(initialNewParam);
  }, [newParam, parameters, onChange]);

  const handleDelete = useCallback(
    (key: string) => {
      onChange(parameters.filter((p) => p.key !== key));
    },
    [parameters, onChange]
  );

  /**
   * Handle selection from dropdown - auto-suggest key only when server path actually changes
   *
   * IMPORTANT: With freeSolo mode, onChange fires on blur even if value didn't change.
   * We must check if serverPath actually changed to avoid overwriting key edits.
   *
   * For Facebook CAPI attribution, specific key names are required:
   * - ip → client_ip (not just 'ip')
   * - userAgent → client_user_agent (not just 'userAgent')
   * - facebook.fbp → facebook.fbp (with dot, not 'fbp')
   * - facebook.fbc → facebook.fbc (with dot, not 'fbc')
   *
   * @see PRESET_SERVER_SOURCES for suggestedKey mappings
   * @see ServerAnalyticsForwarder.enrichUserData() for expected metadata keys
   */
  const handleServerPathSelect = useCallback((value: string | null) => {
    if (!value) return;

    setNewParam((prev) => {
      // If server path didn't change, don't update anything (preserves key edits)
      if (prev.serverPath === value) {
        return prev;
      }

      // Find preset to get the correct suggestedKey for Facebook CAPI
      const preset = PRESET_SERVER_SOURCES.find((p) => p.path === value);

      // Use suggestedKey from preset if available, otherwise fallback to auto-generation
      const suggestedKey = (preset as { suggestedKey?: string })?.suggestedKey
        || value.split('.').pop()?.replace(/[^a-zA-Z0-9_]/g, '')
        || value.replace(/\./g, '_');

      return {
        serverPath: value,
        key: prev.key || suggestedKey,
      };
    });
  }, []);

  return (
    <Box>
      {/* Header with Add button on the right */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <StorageIcon fontSize="small" sx={{ color: theme.palette.secondary.main }} />
        <Typography variant="body2" color="text.secondary">
          Capture server-injected data (geo location, IP, user agent)
        </Typography>
        <Tooltip title="Server data is automatically injected by Edge Gateway when serving the funnel. This includes geographic information, IP address, and other server-side context.">
          <InfoOutlinedIcon fontSize="small" color="action" />
        </Tooltip>
        {/* Spacer to push button to the right */}
        <Box sx={{ flex: 1 }} />
        {!isAdding && (
          <Button
            data-testid="wp-server-add-btn"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            disabled={disabled}
          >
            Add Server Parameter
          </Button>
        )}
      </Box>

      {/* Parameters Table */}
      {parameters.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: '45%' }}>Server Path</TableCell>
                <TableCell sx={{ fontWeight: 600, width: '45%' }}>Internal Key</TableCell>
                <TableCell align="right" sx={{ width: '10%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {parameters.map((param, index) => (
                <TableRow key={param.key} hover data-testid={`wp-server-row-${index}`}>
                  <TableCell>
                    <Chip
                      label={param.sourceDetails.serverPath}
                      size="small"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        bgcolor: alpha(theme.palette.secondary.main, 0.15),
                        color: theme.palette.secondary.main,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`{{${param.key}}}`}
                      size="small"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        bgcolor: theme.palette.action.selected,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      data-testid={`wp-server-delete-${index}`}
                      size="small"
                      onClick={() => handleDelete(param.key)}
                      disabled={disabled}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add New Row */}
      {isAdding ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 2,
            bgcolor: theme.palette.action.hover,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Vertical layout - one field per row */}
          <Autocomplete
            freeSolo
            options={PRESET_SERVER_SOURCES.map((p) => p.path)}
            value={newParam.serverPath}
            onChange={(_, value) => handleServerPathSelect(value as string)}
            inputValue={newParam.serverPath}
            onInputChange={(_, value, reason) => {
              // Update input value on typing, selection fires onChange separately
              if (reason === 'input' || reason === 'reset') {
                setNewParam((prev) => ({ ...prev, serverPath: value }));
              }
            }}
            size="small"
            fullWidth
            renderInput={(params) => (
              <TextField
                {...params}
                data-testid="wp-server-path-input"
                label="Server Data Path"
                placeholder="geoData.country"
                helperText="Path in SERVER_DATA object"
              />
            )}
            renderOption={(props, option) => {
              const preset = PRESET_SERVER_SOURCES.find((p) => p.path === option);
              return (
                <li {...props}>
                  <Box>
                    <Typography variant="body2">{option}</Typography>
                    {preset && (
                      <Typography variant="caption" color="text.secondary">
                        {preset.label}
                      </Typography>
                    )}
                  </Box>
                </li>
              );
            }}
          />
          <TextField
            data-testid="wp-server-key-input"
            label="Internal Key"
            value={newParam.key}
            onChange={(e) =>
              setNewParam((prev) => ({
                ...prev,
                // Allow dots for facebook.fbp/fbc keys (required by ServerAnalyticsForwarder)
                key: e.target.value.replace(/[^a-zA-Z0-9_.]/g, ''),
              }))
            }
            size="small"
            fullWidth
            required
            error={!newParam.key && newParam.serverPath.length > 0}
            placeholder="country"
            helperText="Used as {{key}} in destinations. Dots allowed for Facebook cookies."
            InputProps={{
              sx: { fontFamily: 'monospace' },
            }}
          />

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button data-testid="wp-server-cancel-btn" size="small" onClick={handleCancelAdd}>
              Cancel
            </Button>
            <Button
              data-testid="wp-server-save-btn"
              size="small"
              variant="contained"
              onClick={handleSaveNew}
              disabled={!newParam.serverPath || !newParam.key}
            >
              Add
            </Button>
          </Box>
        </Box>
      ) : null}

      {/* Empty State */}
      {parameters.length === 0 && !isAdding && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            textAlign: 'center',
            bgcolor: theme.palette.action.hover,
            borderRadius: 1,
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No server data parameters defined yet.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Add parameters like geoData.country, ip, or userAgent.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ServerDataSection;
